/**
 * Baut die Rümpfe von `POST /senders` und `PATCH /senders/{id}` aus dem
 * Formularzustand des Blatts `sender-sheet` (Spec 24c, Oberfläche C) —
 * dieselbe Aufteilung wie `features/documents/document-form.ts`.
 *
 * Normalisierung und Prüfung der Merkmale (IBAN, USt-ID, Domain) macht
 * ausschließlich der Server (`suggestions.py::normalize_*`, Fachregel 1);
 * `parseListInput` trennt hier nur Zeilen **und** Kommas in eine Liste ohne
 * Leereinträge — Eingabeaufbereitung, keine Fachlogik (Spec 24c, Oberfläche
 * C, „ist Eingabeaufbereitung, keine Fachlogik").
 */

import type { SenderCreate, SenderUpdate } from '@/features/documents/api'

export interface SenderFormValues {
  readonly name: string
  readonly category: string
  /** Rohtext aus `sender-aliases`, ein Eintrag je Zeile oder Komma. */
  readonly aliases: string
  readonly ibans: string
  readonly vatId: string
  readonly emailDomains: string
}

export const EMPTY_SENDER_FORM: SenderFormValues = {
  name: '',
  category: '',
  aliases: '',
  ibans: '',
  vatId: '',
  emailDomains: '',
}

/** Zeilen **und** Kommas trennen, trimmen, leere Einträge weg. */
export function parseListInput(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '')
}

export function buildSenderCreateBody(form: SenderFormValues, subjectId: string | null): SenderCreate {
  const name = form.name.trim()
  const vatId = form.vatId.trim()

  return {
    name,
    aliases: parseListInput(form.aliases),
    ibans: parseListInput(form.ibans),
    vat_id: vatId === '' ? null : vatId,
    email_domains: parseListInput(form.emailDomains),
    default_category: form.category === '' ? null : form.category,
    default_subject_id: subjectId,
  }
}

export function buildSenderPatchBody(
  form: SenderFormValues,
  initial: SenderFormValues,
  subjectId: string | null,
  initialSubjectId: string | null,
): SenderUpdate {
  const body: SenderUpdate = {}

  const name = form.name.trim()
  if (name !== initial.name.trim() && name !== '') body.name = name

  if (form.category !== initial.category) body.default_category = form.category === '' ? null : form.category

  if (subjectId !== initialSubjectId) body.default_subject_id = subjectId

  if (form.aliases !== initial.aliases) body.aliases = parseListInput(form.aliases)
  if (form.ibans !== initial.ibans) body.ibans = parseListInput(form.ibans)
  if (form.emailDomains !== initial.emailDomains) body.email_domains = parseListInput(form.emailDomains)

  const vatId = form.vatId.trim()
  if (vatId !== initial.vatId.trim()) body.vat_id = vatId === '' ? null : vatId

  return body
}
